// src/components/looker/admin/LookerTemplateMatrix.tsx
/**
 * /admin/looker-templates, part two: workspaces down, templates across, one
 * checkbox per grant. The default template's column is ticked and locked —
 * every workspace has it. The page does the write and refetches.
 *
 * @module components/looker/admin/LookerTemplateMatrix
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { grantKey } from '@/services/looker/lookerTemplateAccess';
import type { LookerTemplateSummary } from '@/services/looker/lookerTypes';

interface Props {
  templates: LookerTemplateSummary[];
  workspaces: ReadonlyArray<{ id: string; name: string }>;
  /** grantKey(workspaceId, templateId) for every grant. */
  granted: ReadonlySet<string>;
  busy: boolean;
  onToggle: (workspaceId: string, templateId: string, on: boolean) => void;
}

export const LookerTemplateMatrix: React.FC<Props> = ({ templates, workspaces, granted, busy, onToggle }) => {
  const { t } = useTranslation();
  if (workspaces.length === 0) return <p className="px-4 py-6 text-sm text-neutral-800">{t('looker.admin.noWorkspaces')}</p>;
  if (templates.length === 0) return <p className="px-4 py-6 text-sm text-neutral-800">{t('looker.admin.empty')}</p>;

  return (
    <table className="w-full text-sm">
      <thead className="bg-neutral-50 text-left text-neutral-700">
        <tr>
          <th className="sticky left-0 bg-neutral-50 px-4 py-3 font-medium">{t('looker.admin.colWorkspace')}</th>
          {templates.map((tp) => (
            <th key={tp.id} className="px-3 py-3 text-center font-medium">
              <span className="block max-w-[10rem] truncate" title={tp.title}>{tp.title}</span>
              {tp.isDefault && <span className="text-xs font-normal text-neutral-800">{t('looker.admin.default')}</span>}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-neutral-100">
        {workspaces.map((ws) => (
          <tr key={ws.id}>
            <th scope="row" className="sticky left-0 bg-white px-4 py-2 text-left font-normal text-ink-900">{ws.name}</th>
            {templates.map((tp) => {
              const isDefault = tp.isDefault === true;
              return (
                <td key={tp.id} className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-neutral-400 accent-ink-950"
                    aria-label={t('looker.admin.toggle', { template: tp.title, workspace: ws.name })}
                    checked={isDefault || granted.has(grantKey(ws.id, tp.id))}
                    disabled={isDefault || busy}
                    onChange={(e) => onToggle(ws.id, tp.id, e.target.checked)}
                  />
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
